import { useCallback, useEffect, useRef, useState } from "react";
import { PageLayout } from "@/ui/layouts";
import { Button, Card, Loader } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import type { TAsyncState, TYardiImportStatus, TYardiRevalidation } from "@/store";
import type { TApiYardiFileUploadResponse, TApiYardiGenerateResponse } from "@/apis/proda_finance";
import {
    AlertTriangle,
    CheckCircle2,
    ChevronRight,
    Circle,
    Import,
    Loader2,
    RefreshCw,
    Upload,
} from "lucide-react";
import { cn } from "@/utils/utils";

/**
 * Human-readable labels for the 4 derived settings tabs.
 * The status API also carries file-level labels, which the view renders directly.
 */
const TAB_LABELS: Record<string, string> = {
    books: "Books",
    chart_of_accounts: "Chart of Accounts",
    property_codes: "Property Codes",
    segments: "Segments",
};

const tabLabel = (tab: string): string => TAB_LABELS[tab] ?? tab;

const formatDateTime = (value: string | null): string => {
    if (!value) return "Never";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

/**
 * View parameters (injected dependencies)
 */
export type TViewYardiImportParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useStatus: () => TAsyncState<TYardiImportStatus>;
        useRevalidation: () => TYardiRevalidation;
        onRefresh: () => void;
        onUploadFile: (sourceTable: string, file: File) => Promise<TApiYardiFileUploadResponse>;
        onGenerate: (tab: string) => Promise<TApiYardiGenerateResponse>;
        onRevalidateAll: () => Promise<void>;
        permissions: () => string[];
    };
};

/**
 * View props (from router/parent)
 */
export type TViewYardiImportProps = Record<string, never>;

type TNotification = {
    type: "success" | "error" | "warning";
    message: string;
    details?: string;
};

/**
 * Collapsed-by-default disclosure for a tab's generation warnings.
 *
 * A degraded run routinely produces dozens of near-identical rows, which expanded
 * inline would push the tab's Generate button off screen and bury the three other
 * tabs. Collapsed, the summary still carries both things a user acts on — how many
 * rows are affected, and whether they were imported or merely preceded a failure —
 * so nothing is hidden except the per-row detail.
 *
 * Own component rather than inline JSX because the open/closed state is per tab and
 * hooks cannot be called from inside the tab `.map()`.
 */
const GenerationWarnings: React.FC<{
    tab: string;
    warnings: string[];
    warningCount: number;
    failed: boolean;
}> = ({ tab, warnings, warningCount, failed }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // The API caps `warnings`; `warningCount` is the true total, so the summary
    // counts every affected row even though only the capped slice can be listed.
    const summary = failed
        ? // A failed run imported nothing (a parse reject returns before any write),
          // so the copy must not imply it did — these rows only explain how far the
          // run got.
          `${warningCount} ${warningCount === 1 ? "row had" : "rows had"} missing values before the run failed`
        : `${warningCount} ${warningCount === 1 ? "row" : "rows"} imported with fallback values`;

    return (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 text-amber-800">
            <button
                type="button"
                aria-expanded={isExpanded}
                data-testid={`yardi-warnings-toggle-${tab}`}
                onClick={() => setIsExpanded((open) => !open)}
                className="flex w-full items-center gap-2 p-3 text-left text-xs font-medium hover:bg-amber-100"
            >
                <ChevronRight
                    className={cn(
                        "h-3 w-3 shrink-0 transition-transform",
                        isExpanded && "rotate-90",
                    )}
                />
                <span>{summary}</span>
                <span className="ml-auto font-normal underline">
                    {isExpanded ? "Hide" : "Show"} details
                </span>
            </button>
            {isExpanded && (
                <div className="px-3 pb-3">
                    <p className="text-xs">Check these rows in the source file:</p>
                    <ul className="mt-1 list-disc pl-4 text-xs">
                        {warnings.map((warning, i) => (
                            <li key={i}>{warning}</li>
                        ))}
                    </ul>
                    {warningCount > warnings.length && (
                        <p className="mt-1 text-xs">
                            Showing {warnings.length} of {warningCount} — see the logs for the full
                            list.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * Creates the Yardi Import view component.
 *
 * Lets a user upload the 14 raw Yardi export files one by one and generate the
 * 4 derived settings tabs (Books, Chart of Accounts, Property Codes, Segments).
 */
export const createViewYardiImport = ({ useCases }: TViewYardiImportParams) => {
    const ViewYardiImport: React.FC<TViewYardiImportProps> = () => {
        const {
            onMount,
            onUnmount,
            useStatus,
            useRevalidation,
            onRefresh,
            onUploadFile,
            onGenerate,
            onRevalidateAll,
            permissions,
        } = useCases();

        const perms = permissions();
        const canUpload = perms.includes("settings_yardi_import_upload");
        const canGenerate = perms.includes("settings_yardi_import_generate");
        const canRevalidate = perms.includes("settings_yardi_import_revalidate");

        const statusState = useStatus();
        const revalidation = useRevalidation();
        const [notification, setNotification] = useState<TNotification | null>(null);
        const [uploadingTable, setUploadingTable] = useState<string | null>(null);
        const [generatingTab, setGeneratingTab] = useState<string | null>(null);
        const [revalidateStarting, setRevalidateStarting] = useState(false);

        // One hidden file input per source table, keyed by source_table
        const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const isLoading = statusState.status === "pending" && !statusState.data;
        const hasError = statusState.status === "rejected" && !statusState.data;
        const status = statusState.data;

        const handleFileChange = useCallback(
            async (sourceTable: string, event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];
                if (!file) return;

                // Reset input so the same file can be re-selected
                const input = fileInputRefs.current[sourceTable];
                if (input) input.value = "";

                setNotification(null);
                setUploadingTable(sourceTable);
                try {
                    const result = await onUploadFile(sourceTable, file);
                    // `success` is only true once the parse has actually finished
                    // (see wireSettingsYardiImport's onUploadFile). A false result can
                    // still mean two very different things: the parse genuinely
                    // failed, or it is simply still running past the poll's own
                    // timeout — the upload itself was accepted either way, so that
                    // case must not be reported as a hard failure. `result.status`
                    // carries the final polled status for every file, so look up this
                    // slot's own parse_status to tell the two apart.
                    const finalFile = result.status.files.find(
                        (f) => f.source_table === result.source_table,
                    );
                    if (result.success) {
                        setNotification({
                            type: "success",
                            message: `Uploaded ${result.source_table} — ${result.row_count} row(s).`,
                        });
                    } else if (finalFile?.parse_status === "pending") {
                        setNotification({
                            type: "warning",
                            message: `${result.source_table} was uploaded and is still parsing.`,
                            // This page keeps checking status in the background while
                            // it's open, and again on remount if you leave and come
                            // back — see wireSettingsYardiImport's pending-file poll.
                            // No promise beyond that: if the tab is closed entirely,
                            // nothing updates until it's reopened.
                            details:
                                "This is taking longer than expected. This page will keep checking in the background — including if you navigate away and return — so there's no need to upload again.",
                        });
                    } else {
                        setNotification({
                            type: "error",
                            message: `Upload failed for ${result.source_table}.`,
                            details: result.errors.join("; "),
                        });
                    }
                } catch {
                    setNotification({
                        type: "error",
                        message: "Upload failed. Please try again.",
                    });
                } finally {
                    setUploadingTable(null);
                }
            },
            [onUploadFile],
        );

        const handleGenerate = useCallback(
            async (tab: string) => {
                // Generation runs in the background. onGenerate enqueues it and then
                // polls status to completion; the per-tab card reflects the running
                // state and final outcome (counts / error). Here we only surface a
                // toast if the request was rejected up front (e.g. missing files).
                setNotification(null);
                setGeneratingTab(tab);
                try {
                    const response = await onGenerate(tab);
                    const rejected = response.results.find((r) => !r.success);
                    if (rejected && rejected.errors.length > 0) {
                        setNotification({
                            type: "error",
                            message: `Cannot generate ${tabLabel(tab)}.`,
                            details: rejected.errors.join("; "),
                        });
                    }
                } catch {
                    setNotification({
                        type: "error",
                        message: "Generation request failed. Please try again.",
                    });
                } finally {
                    setGeneratingTab(null);
                }
            },
            [onGenerate],
        );

        const handleRevalidateAll = useCallback(async () => {
            // Revalidation runs in the background. onRevalidateAll starts it and
            // polls to completion; the progress panel reflects the running state,
            // final counts, or failure. Only surface a toast if the request itself
            // could not be started.
            setNotification(null);
            setRevalidateStarting(true);
            try {
                await onRevalidateAll();
            } catch {
                setNotification({
                    type: "error",
                    message: "Revalidation request failed. Please try again.",
                });
            } finally {
                setRevalidateStarting(false);
            }
        }, [onRevalidateAll]);

        if (isLoading) {
            return (
                <PageLayout
                    title="Import"
                    description="Upload raw Yardi exports and generate settings"
                    icon={<Import className="h-6 w-6" />}
                >
                    <div className="flex justify-center py-12">
                        <Loader size="lg" />
                    </div>
                </PageLayout>
            );
        }

        if (hasError || !status) {
            return (
                <PageLayout
                    title="Import"
                    description="Upload raw Yardi exports and generate settings"
                    icon={<Import className="h-6 w-6" />}
                >
                    <EmptyState
                        variant="error"
                        title="Failed to load Yardi import status"
                        description="An error occurred while loading the import status"
                        action={{ label: "Try Again", onClick: onRefresh }}
                    />
                </PageLayout>
            );
        }

        // Disable every upload/generate control while any action is in flight —
        // an upload here, a generate triggered here, or a background generation
        // still running per the polled status (e.g. after revisiting the page).
        const anyGenerating = status.tabs.some((t) => t.generationStatus === "generating");
        const revalidationRunning = revalidation.status === "running";
        const anyBusy =
            uploadingTable !== null ||
            generatingTab !== null ||
            anyGenerating ||
            revalidationRunning ||
            revalidateStarting;

        // "Revalidate all files" is offered once master data has been generated and
        // nothing is stale — re-applying stale/half-generated master data to files
        // would validate them against the wrong data.
        const anyGenerated = status.tabs.some((t) => t.generatedAt !== null);
        const anyStale = status.tabs.some((t) => t.isStale);
        // 0 eligible files → nothing to revalidate (also guarded server-side).
        const noEligibleFiles = revalidation.eligible === 0 && !revalidationRunning;
        const revalidateDisabled = anyBusy || anyStale || !anyGenerated || noEligibleFiles;
        const revalidateDisabledReason = !anyGenerated
            ? "Generate master data first"
            : anyStale
              ? "Regenerate stale tabs before revalidating files"
              : anyGenerating
                ? "Generation in progress"
                : noEligibleFiles
                  ? "No files to revalidate"
                  : undefined;

        return (
            <PageLayout
                title="Import"
                description="Upload the raw Yardi export files, then generate the derived settings tabs."
                icon={<Import className="h-6 w-6" />}
            >
                {notification && (
                    <div
                        className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
                            notification.type === "success"
                                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                : notification.type === "warning"
                                  ? "border-amber-200 bg-amber-50 text-amber-800"
                                  : "border-red-200 bg-red-50 text-red-800"
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium">{notification.message}</p>
                                {notification.details && (
                                    <p className="mt-1 text-xs opacity-80">
                                        {notification.details}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setNotification(null)}
                                className="ml-4 text-lg leading-none opacity-60 hover:opacity-100"
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                )}

                {/* Source files upload grid */}
                <Card className="overflow-hidden">
                    <div className="border-b bg-slate-50 px-4 py-3">
                        <h2 className="text-sm font-semibold">Source files</h2>
                        <p className="text-xs text-muted-foreground">
                            Upload each raw Yardi export (.xlsx) below.
                        </p>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="border-b bg-slate-50">
                            <tr>
                                <th className="px-4 py-3 text-left font-semibold">File</th>
                                <th className="px-4 py-3 text-left font-semibold">Status</th>
                                <th className="px-4 py-3 text-right font-semibold">Rows</th>
                                <th className="px-4 py-3 text-left font-semibold">Uploaded</th>
                                <th className="px-4 py-3 text-right font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {status.files.map((file) => {
                                const isUploading = uploadingTable === file.sourceTable;
                                return (
                                    <tr
                                        key={file.sourceTable}
                                        className="border-b last:border-b-0 even:bg-muted/40"
                                    >
                                        <td className="px-4 py-3">
                                            <span className="font-medium">{file.label}</span>
                                            <span className="block font-mono text-xs text-muted-foreground">
                                                {file.sourceTable}
                                            </span>
                                        </td>
                                        <td
                                            className="px-4 py-3"
                                            data-testid={`yardi-file-status-${file.sourceTable}`}
                                        >
                                            {file.parseStatus === "pending" ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                    Parsing…
                                                </span>
                                            ) : file.parseStatus === "failed" ? (
                                                // Checked before `!file.present`: a slot whose
                                                // first-ever upload fails to parse never gets a
                                                // storage_path, so `present` stays false forever.
                                                // Without this ordering that case renders as the
                                                // silent "Not uploaded" badge instead of surfacing
                                                // the failure.
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700"
                                                    title={file.parseError ?? undefined}
                                                >
                                                    <AlertTriangle className="h-3 w-3" />
                                                    Parse error
                                                </span>
                                            ) : !file.present ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                                                    <Circle className="h-3 w-3" />
                                                    Not uploaded
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700">
                                                    <CheckCircle2 className="h-3 w-3" />
                                                    Uploaded
                                                </span>
                                            )}
                                            {file.parseStatus === "failed" && file.parseError && (
                                                <span className="mt-1 block max-w-xs truncate text-xs text-red-600">
                                                    {file.parseError}
                                                </span>
                                            )}
                                        </td>
                                        <td
                                            className="px-4 py-3 text-right font-mono"
                                            data-testid={`yardi-file-rows-${file.sourceTable}`}
                                        >
                                            {file.present ? file.rowCount.toLocaleString() : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                            {formatDateTime(file.uploadedAt)}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <input
                                                ref={(el) => {
                                                    fileInputRefs.current[file.sourceTable] = el;
                                                }}
                                                data-testid={`yardi-upload-input-${file.sourceTable}`}
                                                type="file"
                                                accept=".xlsx"
                                                className="hidden"
                                                onChange={(e) => {
                                                    void handleFileChange(file.sourceTable, e);
                                                }}
                                            />
                                            {canUpload && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        fileInputRefs.current[
                                                            file.sourceTable
                                                        ]?.click()
                                                    }
                                                    disabled={anyBusy}
                                                >
                                                    {isUploading ? (
                                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <Upload className="mr-1 h-3 w-3" />
                                                    )}
                                                    {file.present ? "Replace" : "Upload"}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>

                {/* Derived tabs */}
                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {status.tabs.map((tab) => {
                        const isGenerating =
                            generatingTab === tab.tab || tab.generationStatus === "generating";
                        const isParsing = tab.parsingFiles.length > 0;
                        // A slot whose first-ever upload is still parsing appears in both
                        // missingFiles (nothing staged yet) and parsingFiles (a parse is in
                        // flight) for the same tab. Parsing is the more current, more
                        // informative reason, so it takes over the "why isn't this ready"
                        // messaging and the file is excluded from the missing-files list to
                        // avoid reporting the same source table under two reasons at once.
                        const missingNotParsing = tab.missingFiles.filter(
                            (f) => !tab.parsingFiles.includes(f),
                        );
                        const disabledReason = isParsing
                            ? `Parsing: ${tab.parsingFiles.join(", ")}`
                            : !tab.ready
                              ? missingNotParsing.length > 0
                                  ? `Missing files: ${missingNotParsing.join(", ")}`
                                  : tab.blockedBy.length > 0
                                    ? `Blocked by: ${tab.blockedBy.join(", ")}`
                                    : "Not ready"
                              : undefined;

                        return (
                            <Card
                                key={tab.tab}
                                data-testid={`yardi-tab-${tab.tab}`}
                                className="flex flex-col p-4"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <h3 className="flex items-center gap-2 font-semibold">
                                            {tabLabel(tab.tab)}
                                            {tab.isStale && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    Stale
                                                </span>
                                            )}
                                        </h3>
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            Last generated: {formatDateTime(tab.generatedAt)}
                                        </p>
                                    </div>
                                    {tab.ready ? (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700">
                                            <CheckCircle2 className="h-3 w-3" />
                                            Ready
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                                            <Circle className="h-3 w-3" />
                                            Not ready
                                        </span>
                                    )}
                                </div>

                                {isParsing && (
                                    <p className="mt-3 flex items-center gap-2 text-xs text-blue-700">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Parsing: {tab.parsingFiles.join(", ")}
                                    </p>
                                )}
                                {!tab.ready && missingNotParsing.length > 0 && (
                                    <p className="mt-3 text-xs text-red-600">
                                        Missing files: {missingNotParsing.join(", ")}
                                    </p>
                                )}
                                {tab.blockedBy.length > 0 && (
                                    <p className="mt-1 text-xs text-red-600">
                                        Blocked by: {tab.blockedBy.join(", ")}
                                    </p>
                                )}

                                {isGenerating && (
                                    <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Generating… this can take a while for large files.
                                    </p>
                                )}
                                {!isGenerating &&
                                    tab.generationStatus === "failed" &&
                                    tab.generationError && (
                                        <p className="mt-3 text-xs text-red-600">
                                            {tab.generationError}
                                        </p>
                                    )}
                                {!isGenerating &&
                                    tab.counts &&
                                    Object.keys(tab.counts).length > 0 && (
                                        <p className="mt-3 text-xs text-muted-foreground">
                                            {Object.entries(tab.counts)
                                                .map(([key, value]) => `${key}: ${value}`)
                                                .join(", ")}
                                        </p>
                                    )}
                                {!isGenerating && tab.warnings.length > 0 && (
                                    <GenerationWarnings
                                        tab={tab.tab}
                                        warnings={tab.warnings}
                                        warningCount={tab.warningCount}
                                        failed={tab.generationStatus === "failed"}
                                    />
                                )}

                                {canGenerate && (
                                    <div className="mt-4" title={disabledReason}>
                                        <Button
                                            size="sm"
                                            data-testid={`yardi-generate-${tab.tab}`}
                                            onClick={() => {
                                                void handleGenerate(tab.tab);
                                            }}
                                            disabled={
                                                !tab.ready || anyBusy || isGenerating || isParsing
                                            }
                                        >
                                            {isGenerating ? (
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            ) : (
                                                <Import className="mr-2 h-4 w-4" />
                                            )}
                                            Generate
                                        </Button>
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>

                {/* Revalidate all files */}
                {canRevalidate && anyGenerated && (
                    <Card className="mt-6 p-4" data-testid="yardi-revalidate-all">
                        <div className="flex flex-col gap-1">
                            <h3 className="flex items-center gap-2 font-semibold">
                                <RefreshCw className="h-4 w-4" />
                                Revalidate files
                            </h3>
                            <p className="text-xs text-muted-foreground">
                                Re-apply the generated master data (Chart of Accounts, Property
                                Codes, Segments, Books) to all existing files. Files are checked in
                                the background and only those whose validation actually changes are
                                updated.
                            </p>
                        </div>

                        {revalidation.status === "running" && (
                            <p
                                className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"
                                data-testid="yardi-revalidate-progress"
                            >
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Revalidating {revalidation.processed} of {revalidation.total} files…
                                {revalidation.changed > 0 && ` (${revalidation.changed} changed)`}
                            </p>
                        )}
                        {revalidation.status === "completed" && (
                            <p
                                className="mt-3 text-xs text-emerald-700"
                                data-testid="yardi-revalidate-progress"
                            >
                                Revalidated {revalidation.total} file(s). {revalidation.changed}{" "}
                                updated
                                {revalidation.failed > 0 && `, ${revalidation.failed} failed`}.
                            </p>
                        )}
                        {revalidation.status === "failed" && (
                            <p
                                className="mt-3 text-xs text-red-600"
                                data-testid="yardi-revalidate-progress"
                            >
                                Revalidation was interrupted. Please try again.
                            </p>
                        )}

                        <div className="mt-4" title={revalidateDisabledReason}>
                            <Button
                                size="sm"
                                data-testid="yardi-revalidate-all-button"
                                onClick={() => {
                                    void handleRevalidateAll();
                                }}
                                disabled={revalidateDisabled}
                            >
                                {revalidationRunning || revalidateStarting ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                )}
                                Revalidate all files
                            </Button>
                        </div>
                    </Card>
                )}
            </PageLayout>
        );
    };

    return ViewYardiImport;
};
